import Immutable from "immutable";
import exactMath from "exact-math";
import { WEEKDAYS } from "lib/date";
import { floatToMoney } from "lib/money";

import * as studentLinked from "lib/appserver/entityHeaders/studentLinked";

import DinnerItem from "state/school_money/entities/DinnerBooking/DinnerItem";
import BookedOption from "state/school_money/entities/DinnerBooking/BookedOption";

export const DinnerBooking = new Immutable.Record({
  ...studentLinked.defaultProperties,
  remotePaymentID: undefined,
  remoteInstallmentID: undefined,
  type: undefined,
  description: undefined,
  startDate: undefined,
  endDate: undefined,
  isCashless: undefined,
  availableOptions: undefined,
  fsmEligibility: undefined,
  fsmLimitPerDay: undefined,
  lastModifiedDate: undefined,
  bookedPrice: undefined,
  bookedOptions: undefined,
  selectedOptionIDs: undefined,
  dates: undefined,
  holidays: undefined,
  expired: undefined,
  totalPaid: undefined
}, "Dinner Booking");

function buildDinnerBookingFromAppserver(rawPayment) {
  const rawDinnerBooking = rawPayment.payload;
  // TODO: fix on server end (hopefully?)
  rawPayment.student_fk = rawDinnerBooking.student_fk;
  const props = {
    ...studentLinked.appserverAdapter(rawPayment),
    remotePaymentID: rawDinnerBooking.payment_fk,
    remoteInstallmentID: rawDinnerBooking.installment_fk,
    type: rawDinnerBooking.dinner_type,
    description: rawDinnerBooking.description,
    startDate: rawDinnerBooking.dinner_start_date,
    endDate: rawDinnerBooking.dinner_end_date,
    isCashless: isCashless(rawDinnerBooking),
    availableOptions: buildAvailableOptionsFromAppserver(rawDinnerBooking),
    fsmEligibility: getFSMEligibility(rawDinnerBooking),
    fsmLimitPerDay: floatToMoney(rawDinnerBooking.fsm_limit_per_day),
    lastModifiedDate: rawDinnerBooking.last_modified_date_time,
    bookedPrice: floatToMoney(rawDinnerBooking.previous_amount),
    bookedOptions: buildBookedOptionsFromAppserver(rawDinnerBooking),
    dates: buildDatesFromAppserver(rawDinnerBooking),
    holidays: buildHolidaysFromAppserver(rawDinnerBooking),
    expired: buildExpiredFromAppserver(rawDinnerBooking),
    totalPaid: floatToMoney(rawDinnerBooking.total_paid)
  };
  props.selectedOptionIDs = props.bookedOptions.map(option => option.id);
  return DinnerBooking(props);
}

function buildAvailableOptionsFromAppserver(rawDinnerBooking) {

  let availableOptions;

  if (isCashless(rawDinnerBooking)) {
    availableOptions = new Immutable.List( rawDinnerBooking.dinner_items.map(DinnerItem.fromAppserverCashless) );
  } else {
    if (rawDinnerBooking.dinner_type === "MEATVEG") {
      availableOptions = new Immutable.List([
        DinnerItem.fromAppserverNonCashless(rawDinnerBooking, "NONE"),
        DinnerItem.fromAppserverNonCashless(rawDinnerBooking, "MEAT"),
        DinnerItem.fromAppserverNonCashless(rawDinnerBooking, "VEG"),
        DinnerItem.fromAppserverNonCashless(rawDinnerBooking, "SANDWICH")
      ]);
    } else {
      availableOptions = new Immutable.List([
        DinnerItem.fromAppserverNonCashless(rawDinnerBooking, "NONE"),
        DinnerItem.fromAppserverNonCashless(rawDinnerBooking, "MEAL"),
        DinnerItem.fromAppserverNonCashless(rawDinnerBooking, "SANDWICH")
      ]);
    }
  }

  return new Immutable.List(WEEKDAYS)
    .map((weekdayname, weekdayIndex) => {
      return availableOptions
        .filter(option => option.availability.get(weekdayIndex))
        .map(option => {

          let { price } = option;

          const isFSMEligible = getFSMEligibility(rawDinnerBooking).get(weekdayIndex);
          if (isFSMEligible && option.allowFSMPurchase) {
            const fsmLimit = floatToMoney(rawDinnerBooking.fsm_limit_per_day);
            if (fsmLimit === null || !isCashless(rawDinnerBooking)) {
              // FSM eligible and non-cashless or unlimited - item is free
              price = 0;
            } else {
              // FSM eligible, cashless with limit - limit is subtracted from price
              price = Math.max(0, exactMath.sub(price, fsmLimit));
            }
          }

          return option.set("price", price);

        });
    });

}

function buildBookedOptionsFromAppserver(rawDinnerBooking) {
  const fsmStatuses = getFSMEligibility(rawDinnerBooking);
  const rawBookedOptions = new Immutable.List([
    rawDinnerBooking.monday_booking,
    rawDinnerBooking.tuesday_booking,
    rawDinnerBooking.wednesday_booking,
    rawDinnerBooking.thursday_booking,
    rawDinnerBooking.friday_booking,
  ]);
  return rawBookedOptions.map((rawBookedOption, weekdayIndex) => {
    if (isHoliday(rawBookedOption)) {
      return BookedOption();
    }
    return BookedOption.fromAppserver(rawDinnerBooking, rawBookedOption, fsmStatuses.get(weekdayIndex));
  });
}

function buildDatesFromAppserver(rawDinnerBooking) {
  return getBookingDays(rawDinnerBooking).map(({ dinner_date }) => dinner_date);
}

function buildHolidaysFromAppserver(rawDinnerBooking) {
  return getBookingDays(rawDinnerBooking).map(isHoliday);
}

function buildExpiredFromAppserver(rawDinnerBooking) {
  return getBookingDays(rawDinnerBooking).map(({ expired }) => expired);
}

export function isCashless(rawDinnerBooking) {
  return rawDinnerBooking.dinner_type === "CASHLESS";
}

export function isHoliday(rawBookingDay) {
  return rawBookingDay.status === "HOLIDAY";
}

export function isFSMEligible(status) {
  return status !== "PAID";
}

function getBookingDays(rawDinnerBooking) {
  return new Immutable.List([
    rawDinnerBooking.monday_booking,
    rawDinnerBooking.tuesday_booking,
    rawDinnerBooking.wednesday_booking,
    rawDinnerBooking.thursday_booking,
    rawDinnerBooking.friday_booking,
  ]);
}

function getFSMEligibility(rawDinnerBooking) {
  return new Immutable.List([
    rawDinnerBooking.monday_fsm_status,
    rawDinnerBooking.tuesday_fsm_status,
    rawDinnerBooking.wednesday_fsm_status,
    rawDinnerBooking.thursday_fsm_status,
    rawDinnerBooking.friday_fsm_status
  ]).map(isFSMEligible);
}

export { DinnerItem, BookedOption };
DinnerBooking.DinnerItem = DinnerItem;
DinnerBooking.BookedOption = BookedOption;
DinnerBooking.fromAppserver = buildDinnerBookingFromAppserver;
export default DinnerBooking;



// WEBPACK FOOTER //
// ./src/state/school_money/entities/DinnerBooking/index.js